import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';
import { UpdateStepEntrepriseOrder } from '../../entities/document-pa-update-step-entreprise-order.entity';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';
import { SignatairePaEntity } from '../../entities/signataire-pa.entity';
import { MatTableDataSource } from '@angular/material/table';
import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { DocumentPaService } from '../../services/document-pa-service';
import { mergeMap } from 'rxjs/operators';
import { SignatairePaOrder } from '../../entities/document-pa-signataire-order.entity';

@Component({
    selector: 'gestion-signature-pa',
    templateUrl: 'gestion-signature-pa.html',
    styleUrls: ['gestion-signature-pa.css'],
})
export class GestionSignaturePaComponent {

    euEeData?: UpdateStepEntrepriseOrder;
	currentPaId!: number;
    entreprises: EntrepriseTLR[] = [];
    intervenantsEu: IntervenantTLR[] = [];
    intervenantsEe: IntervenantTLR[] = [];
	signAll = false;
    signataires: MatTableDataSource<SignatairePaEntity> = new MatTableDataSource<SignatairePaEntity>();
    displayedColumnsSignataires: string[] = ['nom', 'prenom', 'entreprise', 'raisonSociale', 'email', 'fonction'];
    displaySubject = new Subject<boolean>();

    @Input() id = 'signatairesPaSpecificDialog';

    private signCommandsSubject = new SubjectRelay<number>();

    @Input()
    set signCommands(obs: Observable<number> | undefined) {
        this.signCommandsSubject.registerSource(obs);
    }
	
	@Output() paChanged = new EventEmitter<number>();

    constructor(
        @Inject(IntervenantService) intervenantService: IntervenantService,
        @Inject(EntrepriseService) entrepriseService: EntrepriseService,
        @Inject(DocumentPaService) private documentPaService: DocumentPaService
    ) {
        this.signCommandsSubject.subscribe((currentPaId) => {
			
            if (currentPaId) {
				this.currentPaId = currentPaId;
	            const euEeData$ = this.documentPaService.getStepEntrepriseData(currentPaId);
	            let intervenants$ = intervenantService.getIntervenantsTLRList();
	            let entreprises$ = entrepriseService.getActiveEntreprisesTLRList();
	            let stepData$ = this.documentPaService.getValidationData(currentPaId);
	
	            const data$ = combineLatest([euEeData$, stepData$, entreprises$, intervenants$]).pipe(
	                mergeMap(([euEeData, stepData, allEntreprises, allIntervenants]) =>
	                    forkJoin({
	                        euEeData: of(euEeData),
	                        stepData: of(stepData),
	                        allEntreprises: of(allEntreprises),
	                        allIntervenants: of(allIntervenants),
	                    })
	                )
	            );
	
	            data$.subscribe(({ euEeData, stepData, allEntreprises, allIntervenants }) => {
	                this.euEeData = euEeData;
	
	                if (stepData?.signataires?.length) {
	
	                    const enrichedSignataires =
	                        this.findSignatairesWithEntreprise(
	                            stepData.signataires,
	                            allIntervenants,
	                            allEntreprises
	                        );
	
	                    this.signataires = new MatTableDataSource<SignatairePaEntity>(
	                        enrichedSignataires
	                    );
	
	                } else {
	                    this.signataires = new MatTableDataSource<SignatairePaEntity>([]);
	                }
	
	                if (this.euEeData) {
		                this.entreprises = [];
		                this.intervenantsEu = [];
		                this.intervenantsEe = [];
		
		                [this.euEeData.idEntrepriseEu, this.euEeData.idEntrepriseEe].forEach(e => {
		                    const entreprise = allEntreprises.find(ent => ent.id === e);
		                    if (entreprise) {
		                        this.entreprises.push(entreprise);
		                    }
		                });
		
		                this.intervenantsEu = allIntervenants.filter(intervenant => intervenant.entrepriseId === this.euEeData?.idEntrepriseEu);
		                this.intervenantsEe = allIntervenants.filter(intervenant => intervenant.entrepriseId === this.euEeData?.idEntrepriseEe);
		
						this.displaySubject.next(true);
					}
	
	            });
			}
        });
    }

    private findSignatairesWithEntreprise(
        signataires: SignatairePaEntity[],
        allIntervenants: IntervenantTLR[],
        allEntreprises: EntrepriseTLR[]
    ): SignatairePaEntity[] {

        return signataires.map(signataire => {
            const intervenant = allIntervenants.find(i => i.id === signataire.id);

            if (!intervenant) {
                return signataire;
            }

            const entreprise = allEntreprises.find(
                e => e.id === intervenant.entrepriseId
            );

            if (!entreprise) {
                return signataire;
            }

            return {
                ...signataire,
                idEntreprise: entreprise.id,
                nomEntreprise: entreprise.nom,
                raisonSociale: entreprise.nom
            };
        });
    }

    onCloseRequested() {
        this.displaySubject.next(false);
    }
	
	onSaveRequested()	{
		
        const payload = new SignatairePaOrder();
        payload.id = this.currentPaId;
        payload.signataires = [];
		if (this.euEeData?.euHasSigned) {
			payload.signataires.push({ 'id': this.euEeData?.idResponsableEu} as SignatairePaEntity);
		}
		if (this.euEeData?.eeHasSigned) {
			payload.signataires.push({ 'id': this.euEeData?.idResponsableEe} as SignatairePaEntity);
		}
		this.documentPaService.signPa(payload).subscribe({
            next: (response) => {
				this.paChanged.emit(this.currentPaId);
				this.displaySubject.next(false);
            },
            error: (err) => {
				alert('Erreur lors de la sauvegarde des informations sur la signature');
                console.error('Erreur lors de la sauvegarde des informations sur la signature, erreur: ', err);
            }
        });
	}

	onCheckAllSignataires(): void {
		if (this.euEeData) {
			this.euEeData.euHasSigned = this.signAll;
			this.euEeData.eeHasSigned = this.signAll;
		}
	}

	refreshSignAll(): void {
		if (this.euEeData) {
			this.signAll = this.euEeData.euHasSigned && this.euEeData.eeHasSigned;
		}
	}


}